import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { ComplianceCaseTypeEnum, DocumentRefreshStatusEnum } from "~/graphql/supergraphEnums";
import {
  GetLatestComplianceProfilesWithFiltersDocument,
  GetLatestComplianceProfilesCountDocument,
} from "~/graphql/supergraphHooks";
import {
  type GetLatestComplianceProfilesWithFiltersQuery,
  type GetLatestComplianceProfilesCountQuery,
  type ProfileFieldEnum,
  type ProfileFilters,
  type ProfileSort,
} from "~/graphql/supergraphTypes";
import { type AddressesType } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/types";
import { getCountryName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { toISOFormat } from "~/utils/datetime";
import { DocumentRefreshStatus } from "./DocumentRefreshCellRenderer/types";
import { RefreshStatus } from "./RefreshStatusCellRenderer/types";
import { type ProfileRowItem, type TextFilter } from "./types";
import { isEntityClientOnboardingProfile } from "./utils";

type ProfileNodeWithAddresses =
  GetLatestComplianceProfilesWithFiltersQuery["latestComplianceProfiles"]["edges"][number]["node"] & {
    addresses?: AddressesType[];
  };

export const rowsFromQuery = (
  data: GetLatestComplianceProfilesWithFiltersQuery,
): ProfileRowItem[] => {
  return data.latestComplianceProfiles.edges.map((edge) => {
    const { node } = edge;
    const name = node.legalName || `${node.firstName ?? ""} ${node.lastName ?? ""}`.trim();
    const roles = Array.isArray(node.roles) ? node.roles : [];

    const activeCasesArray = Array.isArray(node.activeCases) ? node.activeCases : [];

    const escalationCasesArray = activeCasesArray.filter(
      (caseInfo) => caseInfo.caseType === ComplianceCaseTypeEnum.Escalation,
    );

    const refreshStatus = (node.refreshStatus as RefreshStatus | null) ?? null;

    const nextRefreshDueDate = node.refreshSchedule?.nextRefreshDueDate;
    const dateSent = node.refreshCase?.complianceInvitation?.dateSent;

    const tooltipData = {
      nextRefreshDueDate: nextRefreshDueDate ? new Date(nextRefreshDueDate) : undefined,
      requestSentDate: dateSent ? new Date(dateSent) : undefined,
      requestSentCount: node.refreshCase?.complianceInvitation?.sentCount,
    };

    const lastRefreshDate = node.refreshSchedule?.lastRefreshDate;
    const nextRefreshDate = node.refreshSchedule?.nextRefreshDueDate;
    const isEoCoProfile = isEntityClientOnboardingProfile(node.complianceCases);
    const positions = (node.positions || []).map((position) => position.id);
    const domicile = getCountryName((node as ProfileNodeWithAddresses).addresses);

    const documentRefreshSchedules = node.documentRefreshSchedules || [];
    const documentRefreshStatus = node.documentRefreshStatus as DocumentRefreshStatus;
    let nextDocumentExpiry: string | undefined;
    if (documentRefreshSchedules.length > 0) {
      const expiryDates = documentRefreshSchedules
        .map((schedule) => schedule.expiryDate)
        .filter((date): date is string => date != null)
        .map(
          (date) =>
            // The backend always sends date-only strings (YYYY-MM-DD). The JS spec parses
            // these as UTC midnight, shifting the calendar date backwards in timezones west
            // of UTC. Appending T00:00:00 (no timezone suffix) forces local midnight instead.
            new Date(`${date}T00:00:00`),
        )
        .sort((a, b) => a.getTime() - b.getTime());

      if (expiryDates.length > 0) {
        nextDocumentExpiry = expiryDates[0].toISOString();
      }
    }

    const documentRefreshDateSent = node.documentRefreshCase?.complianceInvitation?.dateSent;
    const documentRefreshTooltipData = {
      nextDocumentExpiry: nextDocumentExpiry ? new Date(nextDocumentExpiry) : undefined,
      requestSentDate: documentRefreshDateSent ? new Date(documentRefreshDateSent) : undefined,
      requestSentCount: node.documentRefreshCase?.complianceInvitation?.sentCount,
    };

    return {
      id: node.id,
      name: name || "--",
      profileGuid: node.profileGuid ?? "",
      role: roles,
      isInvestingProfile: node.isInvestingProfile ?? false,
      isEntityClientOnboardingProfile: isEoCoProfile,
      amlEntityTypes: node.amlEntityType ?? "--",
      domicile,
      clients: (node.clients ?? []).map(String),
      activeCases: activeCasesArray.map((caseInfo) => ({
        id: caseInfo.id,
        caseType: caseInfo.caseType,
      })),
      escalationCaseIds: escalationCasesArray.map((caseInfo) => caseInfo.id),
      positions,
      lastRefresh: isEoCoProfile ? "--" : (lastRefreshDate ?? "--"),
      nextRefresh: isEoCoProfile ? "--" : (nextRefreshDate ?? "--"),
      refreshStatus,
      refreshCaseId: node.refreshCase?.id,
      invitationId: node.refreshCase?.complianceInvitation?.id,
      tooltipData,
      documentRefreshStatus,
      nextDocumentExpiry,
      documentRefreshCaseId: node.documentRefreshCase?.id,
      documentRefreshCaseStatus: node.documentRefreshCase?.status,
      documentRefreshCaseSubStatus: node.documentRefreshCase?.subStatus ?? undefined,
      documentInvitationId: node.documentRefreshCase?.complianceInvitation?.id,
      documentRefreshTooltipData,
    };
  });
};

const expandDocumentRefreshStatusFilterValues = (values: readonly string[]): string[] => {
  if (
    values.includes(DocumentRefreshStatusEnum.Active) &&
    !values.includes(DocumentRefreshStatusEnum.NoDocuments)
  ) {
    return [...values, DocumentRefreshStatusEnum.NoDocuments];
  }
  return [...values];
};

const mapColumnFieldToGraphQLField = (field: string): ProfileFieldEnum => {
  const fieldMap: Record<string, ProfileFieldEnum> = {
    name: "name",
    role: "roles",
    amlEntityTypes: "aml_entity_type",
    lastRefresh: "last_refresh_date",
    nextRefresh: "next_refresh_date",
    clients: "client_count",
    refreshStatus: "refresh_status",
    documentRefreshStatus: "document_refresh_status",
    domicile: "domicile",
  };

  const mappedField = fieldMap[field];
  if (!mappedField) {
    throw new Error(`Unknown field: ${field}`);
  }
  return mappedField;
};

const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
): ProfileFilters[] | null => {
  if (!filterModel) {
    return null;
  }
  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }

  const queryFilters: ProfileFilters[] = [];

  for (const [field, filterInfo] of Object.entries(filterModel)) {
    const graphQLField = mapColumnFieldToGraphQLField(field);

    if (filterInfo.conditions) {
      const filterValues = filterInfo.conditions.map(
        (condition: TextFilter) => condition.filter,
      );
      queryFilters.push({ field: graphQLField, filterValues });
    } else if (filterInfo.filterType === "set") {
      const rawValues = filterInfo.values.map(String);
      const filterValues =
        field === "documentRefreshStatus"
          ? expandDocumentRefreshStatusFilterValues(rawValues)
          : rawValues;
      queryFilters.push({
        field: graphQLField,
        filterValues,
      });
    } else if (filterInfo.filterType === "text") {
      queryFilters.push({ field: graphQLField, filterValues: [filterInfo.filter] });
    } else if (filterInfo.filterType === "date") {
      const dateFilter = filterInfo as AgGrid.DateFilter & {
        dateFrom?: string | Date;
        dateTo?: string | Date;
      };
      const filterValues: string[] = [];

      if (dateFilter.dateFrom) {
        filterValues.push(toISOFormat(dateFilter.dateFrom));
      }
      if (dateFilter.dateTo) {
        filterValues.push(toISOFormat(dateFilter.dateTo));
      }

      if (filterValues.length > 0) {
        queryFilters.push({ field: graphQLField, filterValues });
      }
    } else if (filterInfo.filterType === "number") {
      const numberFilter = filterInfo as AgGrid.NumberFilter & {
        filter?: number;
        filterTo?: number;
      };
      const filterValues: string[] = [];

      if (numberFilter.filter !== undefined) {
        filterValues.push(String(numberFilter.filter));
      }
      if (numberFilter.filterTo !== undefined) {
        filterValues.push(String(numberFilter.filterTo));
      }

      if (filterValues.length > 0) {
        queryFilters.push({ field: graphQLField, filterValues });
      }
    } else {
      throw Error("Unsupported filter type");
    }
  }

  return queryFilters;
};

const getSortVars = (sortItems: AgGrid.SortModelItem[]): ProfileSort[] | null => {
  if (!sortItems?.length) {
    return null;
  }

  return sortItems.map((sort) => ({
    field: mapColumnFieldToGraphQLField(sort.colId),
    sortAscending: sort.sort === "asc",
  }));
};

export class ProfilesGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  private lastEndCursor: string | null = null;
  private currentPage = 0;
  private lastFilterModel: AgGrid.FilterModel | null = null;
  private lastSortModel: AgGrid.SortModelItem[] = [];
  private profileId?: number;
  private totalCount: number | null = null;

  constructor(profileId?: number) {
    this.client = client;
    this.profileId = profileId;
  }

  private hasFiltersOrSortsChanged(
    filterModel: AgGrid.FilterModel | null,
    sortModel: AgGrid.SortModelItem[],
  ): boolean {
    const filtersChanged = JSON.stringify(filterModel) !== JSON.stringify(this.lastFilterModel);
    const sortsChanged = JSON.stringify(sortModel) !== JSON.stringify(this.lastSortModel);
    return filtersChanged || sortsChanged;
  }

  private async fetchTotalCount(filters: ProfileFilters[] | null): Promise<number> {
    try {
      const response = await this.client.query<GetLatestComplianceProfilesCountQuery>({
        query: GetLatestComplianceProfilesCountDocument,
        variables: {
          ...(filters?.length ? { filters } : {}),
          ...(this.profileId != null ? { profileId: this.profileId } : {}),
        },
        fetchPolicy: "no-cache",
      });
      return response.data.latestComplianceProfilesCount;
    } catch {
      // If count query fails, return 0 to avoid breaking the grid
      return 0;
    }
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const offset = params.request.startRow ?? 0;
    const limit = (params.request.endRow ?? 0) - offset;
    const requestedPage = Math.floor(offset / limit);

    const { filterModel, sortModel } = params.request;

    const filters = getFilterVars(filterModel);
    const sorts = getSortVars(sortModel);

    const filtersOrSortsChanged = this.hasFiltersOrSortsChanged(filterModel, sortModel);

    if (filtersOrSortsChanged) {
      this.lastEndCursor = null;
      this.currentPage = 0;
      this.lastFilterModel = filterModel ?? null;
      this.lastSortModel = sortModel ?? [];
      this.totalCount = null; // Reset total count when filters change
    } else if (requestedPage === 0) {
      this.lastEndCursor = null;
    }

    const isNextPage = requestedPage > this.currentPage;
    const after = isNextPage && this.lastEndCursor ? this.lastEndCursor : undefined;

    const queryVariables = {
      first: limit,
      ...(after ? { after } : {}),
      ...(filters?.length ? { filters } : {}),
      ...(sorts?.length ? { sorts } : {}),
      ...(this.profileId != null ? { profileId: this.profileId } : {}),
    };

    // Fetch total count if we don't have it or if filters changed
    const fetchCountPromise =
      this.totalCount === null
        ? this.fetchTotalCount(filters)
        : Promise.resolve(this.totalCount);

    void Promise.all([
      this.client.query<GetLatestComplianceProfilesWithFiltersQuery>({
        query: GetLatestComplianceProfilesWithFiltersDocument,
        variables: queryVariables,
        fetchPolicy: "no-cache",
      }),
      fetchCountPromise,
    ])
      .then(([response, totalCount]) => {
        const rows = rowsFromQuery(response.data);
        const pageInfo = response.data.latestComplianceProfiles.pageInfo;

        this.lastEndCursor = pageInfo.endCursor;
        this.currentPage = requestedPage;
        this.totalCount = totalCount;

        // Use the total count for accurate row count
        const rowCount = totalCount;

        if (rowCount === 0) {
          params.api.showNoRowsOverlay();
        } else {
          params.api.hideOverlay();
        }

        params.success({
          rowData: rows,
          rowCount,
        });
      })
      .catch(() => {
        params.fail();
      });
  }
}
